import React from 'react'
import { UserPopup } from '../UserPopup'
import { cnMerge } from '../../utils/twmerge'
import type { User } from '../../models'

interface UserAvatarProps {
  user: User
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  showPopup?: boolean
  popupTrigger?: 'hover' | 'click' | 'both'
  popupPosition?: 'top' | 'bottom' | 'left' | 'right' | 'auto'
  className?: string
  showName?: boolean
  showInitials?: boolean
  clickable?: boolean
  onClick?: (user: User) => void
  onContact?: ((user: User) => void) | undefined
  onViewProfile?: ((user: User) => void) | undefined
}

const sizeClasses = {
  xs: 'h-4 w-4 text-xs',
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-sm',
  xl: 'h-12 w-12 text-base'
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'md',
  showPopup = true,
  popupTrigger = 'both',
  popupPosition = 'auto',
  className,
  showName = false,
  showInitials = true,
  clickable = false,
  onClick,
  onContact,
  onViewProfile
}) => {
  const avatarClasses = cnMerge(
    'rounded-full object-cover flex-shrink-0',
    sizeClasses[size],
    clickable && 'cursor-pointer hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 transition-all',
    className
  )

  const initialsClasses = cnMerge(
    'rounded-full flex items-center justify-center font-medium text-white bg-blue-500 flex-shrink-0',
    sizeClasses[size],
    clickable && 'cursor-pointer hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 transition-all'
  )

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.stopPropagation()
      onClick(user)
    }
  }

  const avatarElement = (
    <div className="flex items-center space-x-2">
      {user.avatar ? (
        <img
          src={user.avatar}
          alt={user.name}
          className={avatarClasses}
          onClick={handleClick}
        />
      ) : (
        showInitials && (
          <div 
            className={initialsClasses}
            onClick={handleClick}
          >
            {getInitials(user.name)}
          </div>
        )
      )}
      
      {showName && (
        <span className="text-sm font-medium text-gray-900 truncate">
          {user.name}
        </span>
      )}
    </div>
  )

  if (showPopup) {
    return (
      <UserPopup
        user={user}
        trigger={popupTrigger}
        position={popupPosition}
        onContact={onContact}
        onViewProfile={onViewProfile}
      >
        {avatarElement}
      </UserPopup>
    )
  }

  return avatarElement
}

// Componente especializado para listas
interface UserAvatarListProps {
  users: User[]
  maxDisplay?: number
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  showPopup?: boolean
  className?: string
  onContact?: ((user: User) => void) | undefined
  onViewProfile?: ((user: User) => void) | undefined
}

export const UserAvatarList: React.FC<UserAvatarListProps> = ({
  users,
  maxDisplay = 3,
  size = 'sm',
  showPopup = true,
  className,
  onContact,
  onViewProfile
}) => {
  const displayedUsers = users.slice(0, maxDisplay)
  const hiddenCount = users.length - maxDisplay

  return (
    <div className={cnMerge("flex -space-x-1", className)}>
      {displayedUsers.map((user) => (
        <UserAvatar
          key={user.id}
          user={user}
          size={size}
          showPopup={showPopup}
          onContact={onContact}
          onViewProfile={onViewProfile}
          className="border border-white"
        />
      ))}
      
      {hiddenCount > 0 && (
        <div className={cnMerge(
          "rounded-full flex items-center justify-center font-medium text-white bg-gray-400 border border-white",
          sizeClasses[size]
        )}>
          +{hiddenCount}
        </div>
      )}
    </div>
  )
}
